<script setup lang="ts">
    import Highcharts from 'highcharts';
    import type { Options, SeriesOptionsType } from 'highcharts';
    import 'highcharts/modules/drilldown';
    import { Chart } from 'highcharts-vue';
    import { computed } from 'vue';
    import { IColumnChart } from '@/js/services/api/widget/types';
    import { __ } from '../../../stores/expressions';
    import BaseChart from './BaseChart.vue';

    const props = defineProps<{
        data: IColumnChart['columnChart'];
    }>();

    const rootLevelName: string = __('chart-main-serie');
    const unitType: string = props.data.unitType == '%' ? __('percentage-character') : '';

    // Main series
    const mainSeries = props.data.data
        .filter((item) => item.parentId === '' || item.parentId === null)
        .map((item) => ({
            id: item.id,
            name: item.name,
            y: props.data.unitType === '%' ? parseFloat(item.value.toFixed(1)) : item.value,
            drilldown:
                props.data.data.filter((x) => x.parentId === item.id).length > 0
                    ? item.id
                    : undefined,
        }))
        .sort((a, b) => b.y - a.y)
        .map((item, index) => ({ ...item, x: index }));

    // All unique parentIds in order to create series for each drilldown
    const nonEmptyParentIds: string[] = Array.from(
        new Set(
            props.data.data
                .map((item) => item.parentId)
                .filter((parentId): parentId is string => !!parentId && parentId !== '')
        )
    );

    // Create drilldown series
    const drilldownSeries: SeriesOptionsType[] = [];

    nonEmptyParentIds.forEach((id: string) => {
        drilldownSeries.push({
            name: 'name' + id,
            id: id,
            data: props.data.data
                .filter((item) => item.parentId === id)
                .map((item) => ({
                    id: item.id,
                    name: item.name,
                    y: props.data.unitType === '%' ? parseFloat(item.value.toFixed(1)) : item.value,
                    drilldown:
                        props.data.data.filter((x) => x.parentId === item.id).length > 0
                            ? item.id
                            : null,
                }))
                .sort((a, b) => b.y - a.y),
        } as SeriesOptionsType);
    });

    // Chart config
    const baseOptions = computed<Options>(() => ({
        chart: {
            type: 'column',
            style: {
                fontFamily: 'inherit',
            },
        },
        title: { text: '' },
        tooltip: {
            enabled: true,
            style: {
                fontFamily: 'inherit',
            },
            shadow: false,
            borderColor: 'var(--chart-background-color)',
            borderWidth: 1,
            formatter: function (this: any) {
                return `${this.point.name}<br/>${this.point.y} ${unitType}`;
            },
        },
        legend: { enabled: false },
        exporting: { enabled: false },
        credits: { enabled: false },
        accessibility: {
            enabled: true,
            keyboardNavigation: {
                enabled: true,
            },
        },
        plotOptions: {
            column: {
                borderRadius: 5,
            },
            series: {
                dataLabels: {
                    enabled: true,
                    style: {
                        fontWeight: 'inherit',
                    },
                },
                states: {
                    hover: {
                        enabled: true,
                    },
                    inactive: {
                        enabled: false,
                    },
                },
            },
        },
        xAxis: {
            type: 'category',
            lineColor: 'var(--chart-background-color)',
            lineWidth: 2,
            labels: {
                style: {
                    fontSize: 12,
                },
                formatter: function (this: any) {
                    const maxChars = 20;
                    return this.value.length > maxChars
                        ? this.value.substring(0, maxChars) + '…'
                        : this.value;
                },
            },
        },
        yAxis: {
            title: {
                text: undefined,
            },
            gridLineDashStyle: 'Dash',
            lineColor: 'var(--chart-background-color)',
            lineWidth: 2,
            labels: {
                style: {
                    fontSize: 12,
                },
            },
        },
        series: [
            {
                name: rootLevelName,
                type: 'column',
                color: 'var(--chart-color)',
                data: mainSeries,
            },
        ],
        drilldown: {
            breadcrumbs: {
                enabled: true,
                format: '{level.name}',
                style: {
                    fontFamily: 'inherit !important',
                },
                buttonTheme: {
                    style: {
                        textDecoration: 'none',
                        color: 'inherit',
                        fontFamily: 'inherit !important',
                    },
                    states: {
                        hover: {
                            fill: 'var(--chart-background-color)',
                        },
                    },
                },
            },
            activeAxisLabelStyle: {
                color: '#red',
                textDecoration: 'none',
                fontWeight: 'inherit',
                cursor: 'pointer',
            },
            activeDataLabelStyle: {
                color: '#1f2937',
                textDecoration: 'none',
                fontWeight: 'inherit',
                cursor: 'pointer',
            },
            series: drilldownSeries,
        },
    }));
</script>

<template>
    <div
        class="Chart__container"
        style="
            --chart-color: var(--color-primary-beet);
            --chart-background-color: var(--color-gray-lightest);
        "
    >
        <BaseChart :has-data="mainSeries.length > 0">
            <Chart :options="baseOptions" :highcharts="Highcharts" />
        </BaseChart>
    </div>
</template>
